/**
 * i18n Provider — Time Tracker App Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * DB-sync: Reads the persisted locale from PowerSync (users table) on mount
 * and writes back via updateUserLocaleMutation when the user changes language.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import {
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@time-tracker/config/locales";
import {
  queryKeys,
  type Session,
  updateUserLocaleMutation,
  VISITOR_ID,
} from "@time-tracker/shared";
import { queryClient } from "@/lib/query-client";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useUserPreferences } from "@/providers/user-preferences-context";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — updates DB + reloads catalog */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /**
   * Reactive translation function.
   *
   * TYPE BARRIER (intentionally narrower than the implementation): it only accepts
   * `MessageDescriptor`, meaning the result of a macro (`` msg`…` ``). A plain string
   * —`t("My Day")`—does NOT compile, and that is the important part: the Lingui extractor
   * only sees macros, so a plain string never enters the catalog and remains in English in
   * the 10 non-English locales without any gate saying so. The implementation (`translate`)
   * still accepts a string and resolves it by hash so nothing breaks at runtime; the
   * type makes it impossible to write.
   */
  t: (message: MessageDescriptor, values?: Record<string, unknown>) => string;
  /**
   * Translates a DYNAMIC key: a string that is unknown at the call site because
   * it comes from a data table (`cmd.titleKey`, `getApiStatusLabelKey(…)`).
   *
   * It is correct only if that exact text is declared with `` msg`…` `` in a MANIFEST
   * the extractor can see — `lib/shortcuts/messages.ts`, `lib/connectivity/messages.ts` —
   * because the id hash then matches and the catalog resolves it. Without a manifest, the
   * string is NOT extracted and the user sees it in English in the 10 non-English locales.
   *
   * It exists so the escape from `t`'s strict type is EXPLICIT and greppable, instead
   * of a silent `as`. Every use must point to its manifest.
   */
  tDynamic: (message: string, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/** An already-imported catalog, ready to be handed to i18n. */
interface FetchedCatalog {
  /** The language that could ACTUALLY be loaded (== requested, except fallback to "en"). */
  locale: SupportedLocale;
  messages: Record<string, string>;
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 *
 * ⚠️ This function is PURE with respect to i18n: it imports and returns, but does NOT call
 * `i18n.load`/`i18n.activate`. It used to, and that was the defect: the side effect
 * on i18n occurred as soon as the import resolved, so the load that won was the one
 * that finished LAST, not the one the user requested last.
 * `applyLocale` decides whether the result is still current, so the side effect
 * must occur AFTER that check.
 */
async function fetchCatalog(locale: SupportedLocale): Promise<FetchedCatalog> {
  try {
    const rawModule: unknown = await import(`../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    return { locale, messages: rawModule.messages };
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    return { locale: "en", messages: rawEnModule.messages };
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

// ─── Provider ─────────────────────────────────────────────────────────────────

/** Per-account localStorage key for the cached locale (instant boot, no leak).*/
// oxlint-disable-next-line lingui/no-unlocalized-strings -- localStorage key, not UI copy
const localeCacheKey = (userId: string) => `@app_locale:${userId}`;

export const I18nProvider: ParentComponent = (props) => {
  const drizzle = useDrizzle();
  const session = useSession();
  const { preferences } = useUserPreferences();

  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  // Bumped after every REAL catalog load. Consumers (`translate`) depend on it so
  // they re-derive when a catalog finishes loading even if the `locale` signal
  // value didn't change. This closes a PRODUCTION-only bug: the module init seeds
  // an EMPTY "en" catalog (`i18n.load("en", {})`); on mount the provider loads the
  // real catalog for the resolved locale, but when that locale is the default "en"
  // `setLocaleSignal("en")` is a NO-OP (same value) → no reactivity → consumers
  // that already rendered keep the empty catalog. In dev the lingui macro inlines
  // each message's default text so strings still render, but a PRODUCTION build
  // strips those defaults, so the whole UI paints raw hash ids for English users.
  // The e2e gate runs against `vite dev`, which masked it. Bumping this signal
  // after each load forces the re-derive the no-op signal set would otherwise skip.
  const [catalogVersion, setCatalogVersion] = createSignal(0);
  const bumpCatalogVersion = () => setCatalogVersion((v) => v + 1);

  // ── Per-account locale (multi-account #18) ──────────────────────────────────
  // The language belongs to the ACTIVE account of THIS tab — each real account
  // AND the guest keep their own. Source of truth for a real account is its synced
  // `user.locale` (preferences); the fallback is a PER-ACCOUNT localStorage cache
  // (keyed by userId, including the guest's VISITOR_ID) so a language NEVER leaks
  // across accounts/guest or between tabs. `lastWrittenLocale` is keyed per-account
  // so a fresh manual choice isn't reverted by that account's async DB round-trip.
  const lastWritten = new Map<string, SupportedLocale>();

  const readCachedLocale = (userId: string): SupportedLocale | null => {
    try {
      const v = localStorage.getItem(localeCacheKey(userId));
      return isSupportedLocale(v) ? v : null;
    } catch {
      return null;
    }
  };

  const writeCachedLocale = (userId: string, value: SupportedLocale): void => {
    try {
      localStorage.setItem(localeCacheKey(userId), value);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }
  };

  /**
   * An account's OWN language, never looking at what is on screen: its
   * per-account cache and, if the account has none, the browser's (the
   * same default used by `onMount`). Falling back to "what is already displayed" was
   * precisely the cross-account leak this canon exists to prevent.
   */
  const ownLocaleOf = (userId: string): SupportedLocale =>
    readCachedLocale(userId) ?? detectBrowserLocale();

  // `applyLocale` is triggered from places that run CONCURRENTLY: the startup `onMount`,
  // the per-account effect (which can re-enter several times while `session` and `preferences`
  // settle), and the language screen's `setLocale`. Without a token, the winning language
  // was the one from the load that RESOLVED last, not the one the user requested last: if
  // the startup catalog import was still in flight when the language was selected,
  // it landed later and reverted everything to English. And on the path of a user who
  // just selected a language, the effect does NOT repair it (there is a pending manual
  // change in `lastWritten`, so it does `return`), meaning the UI stayed in English
  // PERMANENTLY. It is only visible when the import is slow, in other words under load. `applyToken`
  // enforces "last write wins".
  let applyToken = 0;
  // `isLoading` belongs to the two lanes that show it: startup (`onMount`) and the user's change
  // (`setLocale`). Only the newest of them may clear it: a superseded change must not clear the
  // flag of the load still in flight, and a rejected load must not leave it stuck on. The
  // effect-driven `applyLocale` calls below never show loading, so they do not take this token.
  let loadingToken = 0;
  // Unmounting invalidates any in-flight `applyLocale`: its catalog import can land after the
  // provider is gone, and `i18n.activate` would then run against a torn-down tree.
  onCleanup(() => {
    applyToken++;
  });

  /** Loads the catalog and publishes the language (signal + version + <html> dir/lang). */
  const applyLocale = async (target: SupportedLocale): Promise<void> => {
    const token = ++applyToken;
    const fetched = await fetchCatalog(target); // import only: does not touch i18n
    if (token !== applyToken) {
      // Overtaken by a newer request. The catalog is discarded WITHOUT
      // handing it to i18n: activating it here reverted the language already set by
      // the current request. The check comes BEFORE any effect on
      // i18n on purpose — that was exactly the bug.
      return;
    }
    // Activate the language that ACTUALLY came in: if there was a fallback to "en", activating
    // the requested language would leave i18n resolving against a nonexistent catalog.
    i18n.load(fetched.locale, fetched.messages);
    i18n.activate(fetched.locale);
    setLocaleSignal(target);
    bumpCatalogVersion();
    applyDirection(target);
  };

  /**
   * Changes the active locale:
   * 1. Loads the catalog chunk
   * 2. Updates the reactive signal (triggers re-renders)
   * 3. Applies HTML dir attribute
   * 4. Persists to DB if authenticated
   */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    // The language change targets the ACTIVE account of THIS tab.
    const userId = session.userId();
    const isGuest = session.isVisitor() || userId === VISITOR_ID;

    // Mark this as a manually written locale FOR THIS ACCOUNT so its own async DB
    // round-trip (or a stale `preferences()` emission) can't revert the choice.
    lastWritten.set(userId, newLocale);

    // Per-account cache (instant, offline, no cross-account leak). It is written
    // BEFORE touching the signal on purpose: the effect below reacts to
    // `locale()` and resolves the account's language from this cache, so with
    // the write after it, it would read the PREVIOUS value and revert the choice
    // just made.
    writeCachedLocale(userId, newLocale);

    if (newLocale !== locale()) {
      const loading = ++loadingToken;
      setIsLoading(true);
      try {
        await applyLocale(newLocale);
      } finally {
        if (loading === loadingToken) {
          setIsLoading(false);
        }
      }
    }

    // The guest is LOCAL-ONLY: never touch the session-query cache or the DB
    // (it has no synced preferences) — that is the cross-account leak guard.
    if (isGuest) {
      return;
    }

    // Update local TanStack Query cache to prevent loops/stale overrides
    try {
      queryClient.setQueryData<Session>(queryKeys.auth.session(), (old) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          user: {
            ...old.user,
            locale: newLocale,
          },
        };
      });
    } catch (err) {
      console.warn("[i18n] Could not update session cache:", err);
    }

    // Persist preference to DB (real account only).
    const dbInstance = drizzle();
    if (dbInstance) {
      try {
        await updateUserLocaleMutation(dbInstance, userId, newLocale);
      } catch (err) {
        console.warn("[i18n] Could not persist locale to DB:", err);
      }
    }
  };

  // On mount: load the initial locale from the ACTIVE account's per-account cache
  // (instant, offline), falling back to the browser locale. The synced DB
  // preference (real account) refines this in the effect below.
  onMount(() => {
    const init = async () => {
      const loading = ++loadingToken;
      try {
        // The default "en" makes `setLocaleSignal` a no-op → `applyLocale` bumps
        // `catalogVersion` so consumers re-derive now that the REAL catalog is
        // live (prod-build hash-id bug).
        await applyLocale(ownLocaleOf(session.userId()));
      } catch (err) {
        console.warn("[i18n] Failed to initialize i18n:", err);
      } finally {
        if (loading === loadingToken) {
          setIsLoading(false);
        }
      }
    };
    void init();
  });

  // The last account this effect resolved. A change IS the account switch
  // for this tab (or the first startup), which is EXACTLY the window during
  // which the previous account's language remains stuck if nobody replaces it.
  let lastResolvedUserId: string | null = null;

  // React to the ACTIVE account's locale (per-tab). The effect re-runs on an
  // account switch because it reads `session.userId()` / `session.isVisitor()`.
  createEffect(() => {
    const userId = session.userId();
    const isGuest = session.isVisitor() || userId === VISITOR_ID;
    const switchedAccount = lastResolvedUserId !== userId;
    lastResolvedUserId = userId;

    // GUEST: strictly local-only. It has NO synced preferences, and reading
    // `preferences()` here would inherit the PREVIOUS real account's locale —
    // the cross-account leak. Resolve the guest's language ONLY from its own
    // sources: its per-account cache and, if absent, the browser language.
    // (Previously, without a cache it kept the language ON SCREEN, which after a switch
    // is precisely that of the previous real account: the leak this was meant to prevent.)
    if (isGuest) {
      const own = ownLocaleOf(userId);
      if (own !== locale()) {
        void applyLocale(own);
      }
      return;
    }

    // REAL account: synced `user.locale` (preferences) is the source of truth.
    const remoteLocale = preferences()?.locale;
    const pendingForUser = lastWritten.get(userId) ?? null;

    if (!isSupportedLocale(remoteLocale)) {
      // No synced preference: it may still be loading or the
      // `user` row for this account may not have downloaded to its local DB yet (newly
      // created account, with no sync round-trip). When SWITCHING accounts, it must
      // fall back to its own language; otherwise, the tab stays on the previous account's
      // language indefinitely. A pending manual change takes precedence over this.
      if (switchedAccount) {
        const desired = pendingForUser ?? ownLocaleOf(userId);
        if (desired !== locale()) {
          void applyLocale(desired);
        }
      }
      return;
    }

    // If the DB has caught up with this account's manual change, release the block.
    if (remoteLocale === pendingForUser) {
      console.log(`[i18n] 🔄 DB synced with local change: "${remoteLocale}"`);
      lastWritten.delete(userId);
      return;
    }
    // A pending manual change for THIS account suppresses stale DB updates.
    if (pendingForUser !== null) {
      console.log(
        `[i18n] ⏳ Ignoring "${remoteLocale}" from DB due to pending local change to "${pendingForUser}"`,
      );
      if (pendingForUser !== locale()) {
        void applyLocale(pendingForUser);
      }
      return;
    }

    // Otherwise, apply the DB preference if it differs from the current UI.
    if (remoteLocale !== locale()) {
      console.log(
        `[i18n] 🌐 DB preference changed to "${remoteLocale}" (UI is "${locale()}") -> applying`,
      );
      writeCachedLocale(userId, remoteLocale);
      void applyLocale(remoteLocale);
    }
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    catalogVersion(); // Re-derive when a catalog finishes loading (see catalogVersion)
    if (typeof message === "string") {
      // The compiled catalog uses HASH ids (generateMessageId), just like the
      // `msg`...`` macro. A plain string (`t(msg`My Day`)`) would look for id="My Day" and not
      // resolve (it would display English in any language). We compute the same id
      // as the macro so `t(msg`literal`)` translates just like `t(msg`literal`)`,
      // with the text itself as fallback. (Fix for the "language is not reflected" bug.)
      return i18n._({ id: generateMessageId(message), message, values });
    }
    // Values also apply to descriptors (they go INSIDE the descriptor in
    // Lingui's API): without them, messages like msg`...{{date}}...`
    // rendered an empty placeholder ("{}").
    // Note: the intermediate variable is intentional — the lingui-solid extraction plugin
    // crashes on spreads inside i18n._()'s inline object.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider
      value={{ locale, setLocale, isLoading, t: translate, tDynamic: translate }}
    >
      {props.children}
    </I18nContext.Provider>
  );
};
